import React, { useEffect } from 'react';
import { safeArray } from '../../utils/safe';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { PlusCircle, Pencil, Trash, IceCream } from 'lucide-react';
import { toast } from 'react-toastify';
import type { Flavor } from '../../types/@types/flavor';
import {
  getFlavors,
  createFlavor,
  updateFlavor,
  deleteFlavor,
} from '../../services/FlavorService';

interface FlavorFormData {
  name: string;
  available: boolean;
}

const schema = yup.object({
  name: yup.string().required().min(2).max(50),
  available: yup.boolean().required(),
});

function FlavorModal({
  isOpen,
  onClose,
  onSubmit,
  initialData,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: FlavorFormData) => void;
  initialData?: Flavor | null;
}) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FlavorFormData>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      name: initialData?.name ?? '',
      available: initialData?.available ?? true,
    },
  });

  useEffect(() => {
    reset({
      name: initialData?.name ?? '',
      available: initialData?.available ?? true,
    });
  }, [initialData, reset]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-sm">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">
            {initialData ? 'Editar Sabor' : 'Crear Nuevo Sabor'}
          </h2>
          <button onClick={onClose} className="text-gray-500">✕</button>
        </div>
        <form onSubmit={handleSubmit(onSubmit as any)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium">Nombre del Sabor</label>
            <input
              {...register('name')}
              className="mt-1 w-full border rounded px-3 py-2"
            />
            {errors.name && (
              <p className="text-red-600 text-sm">{errors.name.message}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" {...register('available')} id="available" />
            <label htmlFor="available" className="text-sm">
              Disponible
            </label>
          </div>
          <button type="submit" className="w-full bg-blue-600 text-white py-2 rounded">
            {initialData ? 'Guardar Cambios' : 'Crear Sabor'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function Flavors() {
  const queryClient = useQueryClient();
  const { data: flavors } = useQuery({ queryKey: ['flavors'], queryFn: getFlavors });

  const createMutation = useMutation({
    mutationFn: createFlavor,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['flavors'] });
      toast.success('Sabor creado');
    },
    onError: () => toast.error('Error al crear el sabor'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: FlavorFormData }) =>
      updateFlavor(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['flavors'] });
      toast.success('Sabor actualizado');
    },
    onError: () => toast.error('Error al actualizar el sabor'),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteFlavor,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['flavors'] });
      toast.success('Sabor eliminado');
    },
    onError: () => toast.error('Error al eliminar el sabor'),
  });

  const [isOpen, setIsOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<Flavor | null>(null);

  const openNew = () => {
    setEditing(null);
    setIsOpen(true);
  };

  const handleEdit = (flavor: Flavor) => {
    setEditing(flavor);
    setIsOpen(true);
  };

  const handleDelete = (id: string) => {
    if (confirm('¿Eliminar sabor?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleSubmit = (data: FlavorFormData) => {
    if (editing) {
      updateMutation.mutate({ id: editing.id, data });
    } else {
      createMutation.mutate(data);
    }
    setIsOpen(false);
  };

  const toggleAvailable = (flavor: Flavor) => {
    updateMutation.mutate({ id: flavor.id, data: { name: flavor.name, available: !flavor.available } });
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">Gestión de Sabores</h1>
        <button
          onClick={openNew}
          className="flex items-center gap-1 px-3 py-2 bg-blue-600 text-white rounded"
        >
          <PlusCircle size={18} /> Nuevo Sabor
        </button>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {safeArray(flavors).map((flavor) => (
          <div
            key={flavor.id}
            className="bg-white rounded-2xl shadow p-4 flex flex-col"
          >
            <div className="flex justify-between items-start">
              <div className="flex items-center gap-2">
                <IceCream size={20} />
                <span className="font-semibold">{flavor.name}</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-xs">Disponibilidad:</span>
                <input
                  type="checkbox"
                  checked={flavor.available}
                  onChange={() => toggleAvailable(flavor)}
                />
              </div>
            </div>
            <span
              className={`mt-2 text-xs px-2 py-1 rounded-full w-max ${
                flavor.available
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
              }`}
            >
              {flavor.available ? 'Disponible' : 'No Disponible'}
            </span>
            {flavor.updatedAt && (
              <p className="text-xs text-gray-500 mt-1">
                Actualizado: {new Date(flavor.updatedAt).toLocaleDateString()}
              </p>
            )}
            <div className="flex gap-2 mt-3 justify-end">
              <button
                onClick={() => handleEdit(flavor)}
                className="text-blue-600 hover:text-blue-800"
              >
                <Pencil size={16} />
              </button>
              <button
                onClick={() => handleDelete(flavor.id)}
                className="text-red-600 hover:text-red-800"
              >
                <Trash size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>
      <FlavorModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        onSubmit={handleSubmit}
        initialData={editing}
      />
    </div>
  );
}
